import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  LinearProgress,
  Alert,
  Stack,
  Tooltip,
  Divider,
  Avatar,
  InputAdornment,
  TableSortLabel,
  Collapse,
  CardActions,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  QrCode as BarcodeIcon,
  Science as ScienceIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Inbox as InboxIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import api from '../../../../../services/resultEntryService';

export const SampleCollection = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [collectionType, setCollectionType] = useState('all');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTests, setSelectedTests] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedRow, setExpandedRow] = useState(null);

  // Status configuration
  const statusConfig = {
    pending: { color: 'default', label: 'PENDING', icon: <ScheduleIcon fontSize="small" /> },
    collection_in_progress: { color: 'warning', label: 'IN PROGRESS', icon: <PlayArrowIcon fontSize="small" /> },
    partially_collected: { color: 'info', label: 'PARTIAL', icon: <RefreshIcon fontSize="small" /> },
    fully_collected: { color: 'success', label: 'COLLECTED', icon: <CheckIcon fontSize="small" /> },
    received_in_lab: { color: 'primary', label: 'IN LAB', icon: <ScienceIcon fontSize="small" /> },
    testing_in_progress: { color: 'secondary', label: 'TESTING', icon: <ScienceIcon fontSize="small" /> }
  };

  // Fetch collections
  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sample-collection/pending', {
        params: {
          type: collectionType,
          search: searchText
        }
      });
      setCollections(response.data.data || []);
    } catch (error) {
      showSnackbar('Failed to fetch collections', 'error');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [collectionType, searchText]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar({ ...snackbar, open: false });
    }, 3000);
  };

  // Initialize collection
  const handleInitializeCollection = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData);

    try {
      const response = await api.post('/sample-collection/initialize', {
        bill_no: values.bill_no,
        collector: values.collector_id,
        collector_name: values.collector_name
      });

      showSnackbar('Collection initialized successfully');
      setSelectedCollection(response.data.data);
      setActiveStep(1);
      fetchCollections();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to initialize collection', 'error');
    }
  };

  // Collect samples
  const handleCollectSamples = async (event) => {
    event.preventDefault();

    if (!selectedCollection || selectedTests.length === 0) {
      showSnackbar('Please select tests to collect', 'error');
      return;
    }

    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData);

    try {
      const testCollections = selectedTests.map(testId => ({
        test_id: testId,
        collection_time: new Date().toISOString(),
        collection_notes: values.collection_notes,
        sample_type: values.sample_type,
        sample_container: values.sample_container,
        collection_site: values.collection_site
      }));

      await api.post('/sample-collection/collect', {
        collection_id: selectedCollection._id,
        test_collections: testCollections
      });

      showSnackbar('Samples collected successfully');
      setActiveStep(2);
      setSelectedTests([]);
      fetchCollections();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to collect samples', 'error');
    }
  };

  // Receive in lab
  const handleReceiveInLab = async (event) => {
    event.preventDefault();

    if (!selectedCollection) {
      showSnackbar('No collection selected', 'error');
      return;
    }

    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData);

    try {
      const testReceipts = selectedTests.map(testId => ({
        test_id: testId,
        sample_condition: values.sample_condition,
        processing_notes: values.processing_notes,
        storage_location: values.storage_location
      }));

      await api.post('/sample-collection/receive', {
        collection_id: selectedCollection._id,
        test_receipts: testReceipts
      });

      showSnackbar('Samples received in lab successfully');
      setActiveStep(0);
      setSelectedCollection(null);
      setSelectedTests([]);
      setOpenModal(false);
      fetchCollections();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to receive samples', 'error');
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get booking type color
  const getBookingTypeColor = (type) => {
    switch (type) {
      case 'OPD': return 'primary';
      case 'IPD': return 'success';
      case 'INVESTIGATION': return 'warning';
      default: return 'default';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Get status chip
  const StatusChip = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        color={config.color}
        variant="filled"
      />
    );
  };

  const steps = ['Initialize Collection', 'Collect Samples', 'Receive in Lab'];

  // Calculate paginated data
  const paginatedCollections = collections.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Snackbar for notifications */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ mb: 2 }}
        >
          {snackbar.message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <ScienceIcon color="primary" />
              <Typography variant="h6">Sample Collection Dashboard</Typography>
            </Stack>
          }
          action={
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={collectionType}
                  label="Type"
                  onChange={(e) => setCollectionType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="OPD">OPD</MenuItem>
                  <MenuItem value="IPD">IPD</MenuItem>
                  <MenuItem value="INVESTIGATION">Investigation</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search by barcode, patient, UHID..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ width: 300 }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedCollection(null);
                  setActiveStep(0);
                  setOpenModal(true);
                }}
              >
                New Collection
              </Button>
            </Stack>
          }
        />

        <CardContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Barcode</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>UHID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Pending Tests</TableCell>
                  <TableCell>Collection Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCollections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No collections found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCollections.map((collection) => (
                    <React.Fragment key={collection._id}>
                      <TableRow hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <BarcodeIcon fontSize="small" />
                            <Typography variant="body2" fontWeight="medium">
                              {collection.barcode_no}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{collection.patient?.name}</TableCell>
                        <TableCell>{collection.uhid}</TableCell>
                        <TableCell>
                          <Chip
                            label={collection.booking_type}
                            size="small"
                            color={getBookingTypeColor(collection.booking_type)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <StatusChip status={collection.overall_status} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={collection.pending_count}
                            size="small"
                            color="warning"
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(collection.collection_session?.start_time)}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Collect">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setSelectedCollection(collection);
                                  setActiveStep(1);
                                  setOpenModal(true);
                                }}
                              >
                                <PlayArrowIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => toggleRowExpansion(collection._id)}
                              >
                                {expandedRow === collection._id ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row - Test Details */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expandedRow === collection._id} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Test Details
                              </Typography>
                              <Grid container spacing={2}>
                                {collection.test_collections?.map((test, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Stack spacing={1}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {test.test_name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Status: {test.test_status}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Collection Time: {formatDate(test.collection_info?.actual_collection_time)}
                                          </Typography>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={collections.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Collection Modal */}
      <Dialog
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedCollection(null);
          setActiveStep(0);
          setSelectedTests([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent dividers>
          {activeStep === 0 && !selectedCollection && (
            <Box component="form" onSubmit={handleInitializeCollection} sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="bill_no"
                    label="Bill Number"
                    fullWidth
                    required
                    helperText="Enter the bill number from OPD/IPD/Investigation"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="collector_name"
                    label="Collector Name"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="collector_id"
                    label="Collector ID"
                    fullWidth
                    helperText="Your user ID for tracking"
                  />
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 3, px: 0 }}>
                <Button onClick={() => setOpenModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  Initialize Collection
                </Button>
              </DialogActions>
            </Box>
          )}

          {selectedCollection && activeStep === 1 && (
            <Box component="form" onSubmit={handleCollectSamples}>
              {/* Collection Summary */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Barcode
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCollection.barcode_no}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Patient
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCollection.patient?.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        UHID
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedCollection.uhid}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Collection Details */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="collection_notes"
                    label="Collection Notes"
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Sample Type</InputLabel>
                    <Select name="sample_type" label="Sample Type" defaultValue="whole_blood">
                      <MenuItem value="whole_blood">Whole Blood</MenuItem>
                      <MenuItem value="serum">Serum</MenuItem>
                      <MenuItem value="plasma">Plasma</MenuItem>
                      <MenuItem value="urine">Urine</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Tests Table */}
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Select Tests to Collect
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTests.length === selectedCollection.test_collections?.length}
                          indeterminate={
                            selectedTests.length > 0 &&
                            selectedTests.length < selectedCollection.test_collections?.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTests(selectedCollection.test_collections?.map(t => t.test_id) || []);
                            } else {
                              setSelectedTests([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Test Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Collection Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCollection.test_collections?.map((test) => (
                      <TableRow key={test.test_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTests.includes(test.test_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTests([...selectedTests, test.test_id]);
                              } else {
                                setSelectedTests(selectedTests.filter(id => id !== test.test_id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{test.test_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={test.test_status?.toUpperCase()}
                            size="small"
                            color={test.test_status === 'collected' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          {test.collection_info?.actual_collection_time
                            ? new Date(test.collection_info.actual_collection_time).toLocaleString()
                            : 'Not collected'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <DialogActions sx={{ px: 0 }}>
                <Button onClick={() => setActiveStep(0)}>
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={selectedTests.length === 0}
                >
                  Collect Selected Tests ({selectedTests.length})
                </Button>
              </DialogActions>
            </Box>
          )}

          {selectedCollection && activeStep === 2 && (
            <Box component="form" onSubmit={handleReceiveInLab}>
              {/* Progress Summary */}
              <Card sx={{ mb: 3, bgcolor: 'success.light' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <CheckIcon color="success" />
                    <Typography variant="body1">
                      <strong>{selectedTests.length} tests collected successfully</strong>
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setActiveStep(1)}
                      sx={{ ml: 'auto' }}
                    >
                      Back to Collection
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Receipt Form */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Sample Condition</InputLabel>
                    <Select name="sample_condition" label="Sample Condition" defaultValue="good">
                      <MenuItem value="good">Good</MenuItem>
                      <MenuItem value="hemolyzed">Hemolyzed</MenuItem>
                      <MenuItem value="clotted">Clotted</MenuItem>
                      <MenuItem value="insufficient">Insufficient</MenuItem>
                      <MenuItem value="contaminated">Contaminated</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="processing_notes"
                    label="Processing Notes"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="storage_location"
                    label="Storage Location"
                    fullWidth
                    defaultValue="Main Lab Storage"
                  />
                </Grid>
              </Grid>

              <DialogActions sx={{ mt: 3, px: 0 }}>
                <Button onClick={() => setActiveStep(1)}>
                  Back
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Receive in Lab
                </Button>
              </DialogActions>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export const SampleRecieved = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchCollectionsForReceipt = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sample-collection/for-lab-receipt');
      setCollections(response.data.data || []);
    } catch (error) {
      showSnackbar('Failed to fetch collections', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollectionsForReceipt();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => {
      setSnackbar({ ...snackbar, open: false });
    }, 3000);
  };

  const handleReceiveSamples = async (event) => {
    event.preventDefault();

    if (!selectedCollection || selectedTests.length === 0) {
      showSnackbar('Please select tests to receive', 'error');
      return;
    }

    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData);

    try {
      const testReceipts = selectedTests.map(testId => ({
        test_id: testId,
        ...values
      }));

      await api.post('/sample-collection/receive', {
        collection_id: selectedCollection._id,
        test_receipts: testReceipts
      });

      showSnackbar('Samples received successfully');
      setOpenModal(false);
      setSelectedCollection(null);
      setSelectedTests([]);
      fetchCollectionsForReceipt();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to receive samples', 'error');
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Calculate paginated data
  const paginatedCollections = collections.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ mb: 2 }}
        >
          {snackbar.message}
        </Alert>
      )}

      <Card>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <InboxIcon color="primary" />
              <Typography variant="h6">Lab Receipt Dashboard</Typography>
            </Stack>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCollectionsForReceipt}
            >
              Refresh
            </Button>
          }
        />

        <CardContent>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Barcode</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Collection Time</TableCell>
                  <TableCell align="center">Pending Receipt</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCollections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No collections pending receipt
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCollections.map((collection) => {
                    const collectedCount = collection.statistics?.collected_tests || 0;
                    const totalCount = collection.statistics?.total_tests || 0;
                    let statusText = 'Not Collected';
                    let statusColor = 'error';

                    if (collectedCount === totalCount) {
                      statusText = 'Ready for Receipt';
                      statusColor = 'success';
                    } else if (collectedCount > 0) {
                      statusText = 'Partial';
                      statusColor = 'warning';
                    }

                    return (
                      <TableRow key={collection._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {collection.barcode_no}
                          </Typography>
                        </TableCell>
                        <TableCell>{collection.patient?.name}</TableCell>
                        <TableCell>
                          {formatDate(collection.collection_session?.start_time)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={collection.pending_receipt_count || 0}
                            color="warning"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusText}
                            size="small"
                            color={statusColor}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Receive Samples">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedCollection(collection);
                                setOpenModal(true);
                              }}
                            >
                              <ArrowForwardIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={collections.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Receive Modal */}
      <Dialog
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setSelectedCollection(null);
          setSelectedTests([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <InboxIcon />
            <Typography variant="h6">Receive Samples in Lab</Typography>
          </Stack>
        </DialogTitle>

        {selectedCollection && (
          <>
            <DialogContent dividers>
              {/* Collection Summary */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Barcode
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedCollection.barcode_no}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Patient
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedCollection.patient?.name}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Collection Time
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(selectedCollection.collection_session?.start_time)}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          Tests for Receipt
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {selectedCollection.pending_receipt_count} tests
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Tests Selection */}
              <Typography variant="subtitle2" gutterBottom>
                Select Tests to Receive
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedTests.length === selectedCollection.test_collections?.length}
                          indeterminate={
                            selectedTests.length > 0 &&
                            selectedTests.length < selectedCollection.test_collections?.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTests(selectedCollection.test_collections?.map(t => t.test_id) || []);
                            } else {
                              setSelectedTests([]);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>Test Name</TableCell>
                      <TableCell>Collection Time</TableCell>
                      <TableCell>Sample Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCollection.test_collections?.map((test) => (
                      <TableRow key={test.test_id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTests.includes(test.test_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTests([...selectedTests, test.test_id]);
                              } else {
                                setSelectedTests(selectedTests.filter(id => id !== test.test_id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{test.test_name}</TableCell>
                        <TableCell>
                          {formatDate(test.collection_info?.actual_collection_time)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={test.collection_info?.sample_type || 'N/A'}  // Updated field name
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Receipt Form */}
              <Box component="form" id="receiptForm" onSubmit={handleReceiveSamples}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Sample Condition</InputLabel>
                      <Select name="sample_condition" label="Sample Condition" defaultValue="good">
                        <MenuItem value="good">Good</MenuItem>
                        <MenuItem value="hemolyzed">Hemolyzed</MenuItem>
                        <MenuItem value="clotted">Clotted</MenuItem>
                        <MenuItem value="insufficient">Insufficient</MenuItem>
                        <MenuItem value="contaminated">Contaminated</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="processing_notes"
                      label="Processing Notes"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      name="storage_location"
                      label="Storage Location"
                      fullWidth
                      defaultValue="Main Lab Storage"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Storage Temperature</InputLabel>
                      <Select name="storage_temperature" label="Storage Temperature" defaultValue="room_temp">
                        <MenuItem value="room_temp">Room Temperature</MenuItem>
                        <MenuItem value="refrigerated">Refrigerated (2-8°C)</MenuItem>
                        <MenuItem value="frozen">Frozen (-20°C)</MenuItem>
                        <MenuItem value="deep_frozen">Deep Frozen (-80°C)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedCollection(null);
                  setSelectedTests([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="receiptForm"
                variant="contained"
                disabled={selectedTests.length === 0}
              >
                Receive Selected Samples ({selectedTests.length})
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};